import { Link } from "react-router-dom";
import type { Asset } from "@/data/types";
import { drillHref, type NavState } from "@/navigation/trail";
import { Card, PaneHeader, Totals } from "./parts";

function mb(size: string): number {
  return Number.parseFloat(size) || 0;
}

export default function RevisionTab({
  asset,
  state,
}: {
  asset: Asset;
  state: NavState;
}) {
  const current = asset.versions.at(-1)!;
  const first = asset.versions[0];
  const totalGb = asset.versions.reduce(
    (s, v) => s + Number.parseFloat(v.size),
    0,
  );
  const deltaTri =
    Number.parseFloat(current.triangles) - Number.parseFloat(first.triangles);
  const spanDays = Math.round(
    (new Date(current.date.split("/").reverse().join("-")).getTime() -
      new Date(first.date.split("/").reverse().join("-")).getTime()) /
      86_400_000,
  );

  const totalMb = asset.attachments.reduce((s, f) => s + mb(f.size), 0);
  const onCurrent = asset.attachments.filter(
    (f) => f.version === current.id,
  ).length;
  const byKind = (kind: string) =>
    asset.attachments.filter((f) => f.kind === kind).length;

  return (
    <section className="pane">
      <PaneHeader
        asset={asset}
        h2="Revisione"
        sub={
          <>
            {asset.ref} · {asset.versions.length} VERSIONI DI SCANSIONE ·{" "}
            {asset.attachments.length} FILE ALLEGATI · ULTIMA {current.date}
          </>
        }
      />

      <Card
        title="▦ Versioni di scansione"
        note={`${current.id} corrente · ${spanDays} giorni di intervallo`}
      >
        <div className="tw">
          <table className="pt">
            <thead>
              <tr>
                <th>Versione</th>
                <th>Data</th>
                <th>Autore</th>
                <th>Metodo</th>
                <th className="num">Triangoli</th>
                <th className="num">Dimensione</th>
                <th>Modifiche</th>
                <th>Stato</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...asset.versions].reverse().map((v) => (
                <tr
                  key={v.id}
                  className={v.status === "corrente" ? "cur" : undefined}
                >
                  <td className="mono">{v.id}</td>
                  <td className="mono">{v.date}</td>
                  <td>{v.author}</td>
                  <td>{v.method}</td>
                  <td className="num">{v.triangles}</td>
                  <td className="num">{v.size}</td>
                  <td className="nm">{v.changes}</td>
                  <td>
                    <span
                      className={`st${v.status === "corrente" ? " ok" : ""}`}
                    >
                      {v.status}
                    </span>
                  </td>
                  <td>
                    <Link
                      className="simili"
                      to={drillHref(asset.ref, state, "version", v.id)}
                    >
                      ✦ simili
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Totals
          items={[
            ["Versioni", String(asset.versions.length)],
            ["Spazio occupato", `${totalGb.toFixed(2)} GB`],
            [
              "Δ triangoli",
              `${deltaTri >= 0 ? "+" : ""}${deltaTri.toFixed(1)} M`,
            ],
            ["Intervallo", `${spanDays} giorni`],
          ]}
        />
      </Card>

      <Card
        title="▦ File allegati"
        note={`${asset.attachments.length} file · ${byKind("Render")} render · ${byKind("CAD")} CAD · ${byKind("Disegno tecnico")} disegni · ${byKind("Foto riferimento")} foto`}
      >
        <div className="tw">
          <table className="pt">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Nome file</th>
                <th>Formato</th>
                <th>Versione</th>
                <th className="num">Dimensione</th>
                <th>Data</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {asset.attachments.map((f) => (
                <tr key={f.name}>
                  <td>{f.kind}</td>
                  <td className="nm">{f.name}</td>
                  <td className="mono">{f.format}</td>
                  <td className="mono">{f.version}</td>
                  <td className="num">{f.size}</td>
                  <td className="mono">{f.date}</td>
                  <td>
                    <Link
                      className="simili"
                      to={drillHref(asset.ref, state, "file", f.name)}
                      title={`Trova file simili a ${f.name}`}
                    >
                      ✦ simili
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Totals
          items={[
            ["File", String(asset.attachments.length)],
            ["Dimensione", `${totalMb.toFixed(1)} MB`],
            [`Legati a ${current.id}`, String(onCurrent)],
          ]}
        />
      </Card>

      <Card
        title="◱ Approvazioni"
        note={`${asset.approvals.filter((a) => a.outcome === "in attesa").length} in attesa`}
      >
        <div className="tw">
          <table className="pt">
            <thead>
              <tr>
                <th>Ruolo</th>
                <th>Responsabile</th>
                <th>Versione</th>
                <th>Data</th>
                <th>Esito</th>
              </tr>
            </thead>
            <tbody>
              {asset.approvals.map((a) => {
                let outcomeClass = "st";
                if (a.outcome === "approvata") outcomeClass = "st ok";
                else if (a.outcome === "in deroga") outcomeClass = "st ko";
                return (
                  <tr key={a.role}>
                    <td className="nm">{a.role}</td>
                    <td>{a.owner}</td>
                    <td className="mono">{a.version}</td>
                    <td className="mono">{a.date}</td>
                    <td>
                      <span className={outcomeClass}>{a.outcome}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}
